import { Query } from "@apollo/client/react/components";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { Fragment, useRef, useEffect } from "react";

import { GET_CITIES } from "@api/queries/suggestions";
import Modal from "@components/Modal/Modal";
import busIcon from "@images/svg/bus_v4.svg";
import i from "@styles/icons.module.scss";
import Input from "@ui-kit/Input/Input";
import Loading from "@ui-kit/Loading/Loading";

import s from "./SearchDestinationMobile.module.scss";

const SearchDestinationMobile = ({
  isOpen,
  toggleModal,
  modalTitle,
  search = "",
  updateSearch,
  selectItem,
  popularDestinations = [],
  selectedStop,
}) => {
  const { t } = useTranslation(["common"]);
  const inputRef = useRef(null);
  const { locale } = useRouter();

  useEffect(() => {
    if (inputRef.current) {
      // TODO: try pass ref to input
      const input = inputRef.current.getElementsByTagName("input")[0];
      input.focus();
    }
  }, [inputRef.current]);

  return (
    <Modal className={s.modal} isOpen={isOpen} onRequestClose={toggleModal}>
      <header className={s.header}>
        <h2>{t(modalTitle)}</h2>
      </header>
      <div className={s.input} ref={inputRef}>
        <Input
          value={search}
          label={t("country, city or airport")}
          onChange={updateSearch}
          onFocus={(e) => {
            e.target.select();
          }}
          onClick={(e) => {
            e.target.select();
          }}
        />
      </div>
      <Query
        query={GET_CITIES}
        variables={{
          cityName: search,
          lang: locale,
        }}
        skip={search.length < 3}
      >
        {({ loading, error, data = { cities: [] } }) => {
          if (loading) {
            return (
              <div className={s.suggestions}>
                <Loading />
              </div>
            );
          }

          return (
            <div className={s.suggestions}>
              {data.cities.length ? (
                data.cities.map(({ CityId, CityName, BusStops }) => (
                  <Fragment key={CityId}>
                    <div className={s.townCountry} key={CityId}>
                      <icon className={i.point} />
                      {CityName}
                    </div>
                    {BusStops.map(({ BusStopId, BusStopName, CountryName }) => {
                      const icon = BusStopName.includes("airplane")
                        ? "airplane"
                        : "bus";
                      return (
                        <div
                          key={BusStopId}
                          className={s.item}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            selectItem(
                              {
                                BusStopName,
                                BusStopId,
                                CountryName,
                                CityName,
                              },
                              data.cities,
                            );
                            toggleModal();
                          }}
                        >
                          {icon === "bus" ? (
                            <Image
                              className={s.busIcon}
                              src={busIcon}
                              alt={BusStopName}
                              width={13}
                              height={13}
                            />
                          ) : (
                            <icon className={i[icon]} />
                          )}
                          <span>{BusStopName}</span>
                        </div>
                      );
                    })}
                  </Fragment>
                ))
              ) : (
                <>
                  {search.length >= 3 && (
                    <>
                      <div className={s.townCountry}>{t("search results")}</div>
                      <span className={s.item}>
                        {t("no matches for")} «<b>{search}</b>»
                      </span>
                    </>
                  )}
                  <div className={s.townCountry}>
                    {t("popular destinations")}
                  </div>
                  {popularDestinations.map(({ CityId, CityName, BusStops }) =>
                    BusStops.map(({ BusStopId, BusStopName, CountryName }) => {
                      const icon = BusStopName.includes("airplane")
                        ? "airplane"
                        : "bus";
                      return (
                        selectedStop !== BusStopId && (
                          <div
                            key={BusStopId}
                            className={s.item}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              selectItem(
                                {
                                  BusStopName,
                                  BusStopId,
                                  CountryName,
                                  CityName,
                                },
                                data.cities,
                              );
                              toggleModal();
                            }}
                          >
                            {icon === "bus" ? (
                              <Image
                                className={s.busIcon}
                                src={busIcon}
                                alt={BusStopName}
                                width={13}
                                height={13}
                              />
                            ) : (
                              <icon className={i[icon]} />
                            )}
                            <span>{BusStopName}</span>
                          </div>
                        )
                      );
                    }),
                  )}
                </>
              )}
            </div>
          );
        }}
      </Query>
    </Modal>
  );
};

export default SearchDestinationMobile;
